/**
 * BlockService — client-side source of truth for the local player's blocked list.
 *
 * Hydrated from the server's enriched `blocked:list` event (sent on auth and
 * after every block/unblock) and kept live via friend:blocked / friend:unblocked.
 * Consumers (LobbyService presence/chat filter, SettingsScreen manager, the
 * PlayerCard) read from here so "who have I blocked?" has one answer everywhere.
 *
 * Note: this tracks people *I* blocked. The server independently enforces all
 * interactions both ways (chat, requests, gifts, room joins) and filters search,
 * so a one-directional client set is sufficient for hiding them from my view.
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import type { BlockedPlayer } from '@sdl/shared';

interface BlockServiceEvents {
  'changed': { blocked: BlockedPlayer[] };
}

export class BlockService extends EventEmitter<BlockServiceEvents> {
  private byId: Map<string, BlockedPlayer> = new Map();

  /** Replace the whole list (from the server's authoritative blocked:list). */
  setAll(list: BlockedPlayer[]): void {
    this.byId = new Map(list.map((b) => [b.id, b]));
    this.emit('changed', { blocked: this.list() });
  }

  /** Optimistic add (used at block time before the server list round-trips). */
  add(player: BlockedPlayer): void {
    if (this.byId.has(player.id)) return;
    this.byId.set(player.id, player);
    this.emit('changed', { blocked: this.list() });
  }

  /** Optimistic remove (used at unblock time). */
  remove(playerId: string): void {
    if (this.byId.delete(playerId)) {
      this.emit('changed', { blocked: this.list() });
    }
  }

  has(playerId: string): boolean {
    return this.byId.has(playerId);
  }

  ids(): string[] {
    return Array.from(this.byId.keys());
  }

  list(): BlockedPlayer[] {
    return Array.from(this.byId.values());
  }
}
